import { Tooltip, Typography } from "antd";
import message from "components/CustomMessage";
import DeletePopup from "components/DeletePopup";
import InternalLink from "components/InternalLink";
import NameAvatar from "components/NameAvatar";
import User from "components/User";
import { getVocabulary } from "components/Vocabulary";
import { PermissionSetMap } from "constants/RBACConstants";
import { useFeatureFlag } from "contexts/FeatureFlagContext";
import _, { omit } from "lodash";
import moment from "moment";
import React from "react";
import { useHistory } from "react-router-dom";
import ScanAPI from "restAPI/ScanAPI";
import { NameIdPair } from "types/api/nameIdPair";
import { ScanFindings, ScanSummary } from "types/api/scanSummary";
import {
  FilterChangeCallBack,
  FilterInfo,
  FilterValue,
} from "types/pagination";
import { hasPermission } from "utils/authService";
import ModalUtil from "utils/modalUtil";
import {
  fetchConfigAndRedirect,
  fetchConfigAndRedirectToToolUI,
  nativeRedirectionSupportedTools,
} from "utils/securityToolUtil";
import { CellRenderers, startCaseWithSpace } from "utils/tableUtils";
import { updateURLSearch } from "utils/urlUtil";
import EnvironmentName from "views/GlobalSettings/EnvironmentName";
import { IntegrationToolsData, ToolsData } from "views/Integrations/new/util";
import {
  ERROR_TITLE,
  SCAN_STATUS_COLORS,
  TRIGGER_BY_NAME_MAPPING,
  getProgress,
} from "views/scanresult/ScanListProvider";
import ScanIDCell from "views/scanresult/components/ScanIDCell";
import ScanStats from "views/scanresult/components/ScanStats";
import { DEFAULT_DATE_FORMATS } from "views/scanresult/constants";
import { isSBOMScan } from "views/scanresult/scanUtil";

import ACDropdown, { ACDropdownOptionProps } from "ac-components/ACDropdown";
import ACLink from "ac-components/ACLink/ACLink";
import { ACTableColumnType } from "ac-components/ACTable/ACTable";
import ACButton from "ac-components/ac-button/ACButton";
import DownloadIcon from "ac-components/ac-icons/DownloadIcon";
import EyeIcon from "ac-components/ac-icons/EyeIcon";
import MenuIcon from "ac-components/ac-icons/MenuIcon";
import RedirectIcon from "ac-components/ac-icons/RedirectIcon";
import TrashIcon from "ac-components/ac-icons/TrashIcon";
import ACPopover from "ac-components/ac-popover/ACPopover";
import ACResponsiveTags from "ac-components/ac-responsive-tags/ACResponsiveTags";
import ACTagColored, {
  TagColors,
} from "ac-components/ac-tag-colored/ACTagColored";
import ACTag from "ac-components/ac-tag/ACTag";

import useDateFormats from "./useDateFormats";

const MAX_SCAN_AGE_DAYS = 365;

export default function useScanColumns({
  refresh,
  filterInfo,
  onFilterChange,
}: {
  refresh(): void;
  onFilterChange?: FilterChangeCallBack;
  filterInfo?: FilterInfo;
}): {
  allColumns: ACTableColumnType<ScanSummary>[];
  actionRender: (record: ScanSummary) => React.ReactNode;
} {
  const history = useHistory();
  const featureFlags = useFeatureFlag();

  const applyCellFilter = onFilterChange
    ? (key: React.Key, value: FilterValue) => {
        if (onFilterChange) {
          onFilterChange({ ...filterInfo, [key]: [value] });
        }
      }
    : undefined;

  const checkOnlyDuplicateFindings = (finding: ScanFindings) => {
    return (
      finding.totalFindings &&
      finding.duplicateFindings &&
      finding.totalFindings.count === finding.duplicateFindings.count
    );
  };

  const redirectToFindings = (
    scanId: number,
    finding: string | undefined,
    record: ScanSummary,
  ) => {
    const filters: FilterInfo = {
      [finding === "resolved" ? "mitigatedByScanId" : "scan"]: [`${scanId}`],
      product: [record.product.id!],
      subProduct: [record.subproduct.id!],
      environmentName: [record.environment],
      ...(finding === "resolved" && {
        issueResolved: [
          moment(record.createdAt).utc().format(),
          moment().utc().format(),
        ],
      }),
    };
    updateURLSearch(
      history,
      {
        filters,
        pagination: {
          page: 0,
        },
      },
      "/findings",
    );
  };

  const onDelete = (id: number) => {
    const deleteAPI = () =>
      ScanAPI.deleteScan(id)
        .promise.then(() => {
          message.success(
            "Scans delete is in progress. Please check after some time.",
          );
          refresh();
        })
        .catch((err) => console.error(err));

    ModalUtil.show({
      // @ts-ignore
      content: <DeletePopup confirmText={"Delete"} onDelete={deleteAPI} />,
    });
  };

  const onActionClick = (name: string | number, record: ScanSummary) => {
    switch (name) {
      case "resultDetail": {
        const sbomFlagsEnabled =
          featureFlags.DECOUPLE_SBOM_INGESTION && featureFlags.MANAGE_SBOM;
        if (isSBOMScan(record) && sbomFlagsEnabled) {
          // Redirect to SBOM/components page with product and subProduct filters
          updateURLSearch(
            history,
            {
              filters: {
                product: [record.product.id!],
                subProduct: [record.subproduct.id!],
              },
              pagination: {
                page: 0,
              },
            },
            "/sbom",
          );
        } else {
          redirectToFindings(record.id!, undefined, record);
        }
        break;
      }
      case "exportReport":
        window.location.href = `/api/scans/${record?.id}/files`;
        break;
      case "delete":
        onDelete(record.id!);
        break;
      default:
        break;
    }
  };

  const dateFormatter = useDateFormats({
    prefixKey: "scans",
    defaultToKeys: DEFAULT_DATE_FORMATS,
  });

  const columns: ACTableColumnType<ScanSummary>[] = [
    {
      title: "ID",
      dataIndex: "id",
      key: "id",
      sorter: true,
      render: (id: string, record) => <ScanIDCell id={id} record={record} />,
      fixed: "left",
      width: 300,
    },
    {
      title: "Status",
      dataIndex: "scanStatus",
      key: "scanStatus",
      sorter: true,
      width: 170,
      render: (text, record) => {
        const { progress } = getProgress(record.scanStatus);
        const tag = (
          <ACTagColored
            color={SCAN_STATUS_COLORS[record.scanStatus] as TagColors}
            label={_.capitalize(text)}
          />
        );
        return progress === -1 ? (
          <ACPopover
            style={{ maxWidth: 600 }}
            content={
              _.capitalize(record.errorMessage?.replace(ERROR_TITLE, "")) ??
              "No info available!"
            }
            placement="topRight"
            trigger="hover"
            header={ERROR_TITLE}
          >
            {tag}
          </ACPopover>
        ) : (
          tag
        );
      },
    },
    {
      title: "Change",
      dataIndex: "findings",
      key: "findings",
      render: (text, record) => <ScanStats record={record} />,
      width: 220,
    },
    {
      title: getVocabulary("product"),
      dataIndex: "product",
      key: "product",
      sorter: true,
      render: (record) => renderProductSubproductLink(record, "/product"),
      width: 180,
    },
    {
      title: getVocabulary("subProduct"),
      dataIndex: "subproduct",
      key: "subProduct",
      sorter: true,
      render: (record) => renderProductSubproductLink(record, "/subProduct"),
      width: 180,
    },
    {
      title: "Environment",
      dataIndex: "environmentName",
      key: "environmentName",
      render: (text, record) => (
        <ACTag
          // @ts-expect-error
          label={<EnvironmentName name={record.environment} />}
          mode="label"
        />
      ),
      width: 170,
      sorter: true,
    },
    {
      title: "Source Tool",
      dataIndex: "source",
      key: "source",
      width: 230,
      sorter: true,
      render: (source, record) => {
        const { triggerby, tool, product, subproduct, environment } = record;
        const icon = _.find(
          [...ToolsData, ...IntegrationToolsData],
          (entry: any) => entry.name === tool,
        )?.icon;
        if (triggerby === "UI_UPLOAD") {
          return (
            <div className="text-ellipsis">
              <NameAvatar
                className="m-r-xs"
                color="#A0D274"
                icon={icon}
                name={tool!}
                size="small"
              />
              {tool}
            </div>
          );
        }
        return (
          <div className="text-ellipsis">
            <NameAvatar
              className="m-r-xs"
              color="#A0D274"
              icon={icon}
              name={tool!}
              size="small"
            />
            <ACLink
              primary={false}
              overlay
              onClick={() => {
                fetchConfigAndRedirect({
                  toolName: tool,
                  productName: product!.name,
                  subProductName: subproduct!.name,
                  envName: environment,
                  operationalStatus: undefined,
                });
              }}
            >
              {tool}
            </ACLink>
            {nativeRedirectionSupportedTools.includes(tool!) &&
            triggerby === "SCHEDULED" ? (
              <Typography.Link
                className="m-l-xs"
                onClick={() => {
                  fetchConfigAndRedirectToToolUI({
                    toolName: tool,
                    productName: product!.name,
                    subProductName: subproduct!.name,
                    envName: environment,
                    operationalStatus: undefined,
                  });
                }}
              >
                <RedirectIcon />
              </Typography.Link>
            ) : null}
          </div>
        );
      },
    },
    {
      title: "Type",
      dataIndex: "scanType",
      key: "scanType",
      width: 200,
      render: (scanType) => (
        <ACResponsiveTags
          tags={scanType}
          tagProps={{
            mode: "label",
          }}
          onFilterClick={
            applyCellFilter
              ? (type) => {
                  applyCellFilter("scanType", type);
                }
              : undefined
          }
        />
      ),
    },
    {
      title: "Ingested",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 200,
      sorter: true,
      render: (value) => dateFormatter.render("createdAt", value),
      dateFormatter: dateFormatter,
    },
    {
      title: "Trigger",
      dataIndex: "triggerby",
      key: "triggerby",
      sorter: true,
      width: 180,
      render: (_, record) => (
        <ACTag
          label={
            // @ts-ignore
            TRIGGER_BY_NAME_MAPPING[record.triggerby] ||
            // @ts-ignore
            startCaseWithSpace(record.triggerby)
          }
          mode="label"
        />
      ),
    },
    {
      title: "Uploaded by",
      dataIndex: "uploadedBy",
      key: "uploadedBy",
      width: 150,
      render: (_, record) => (
        <User
          withIcon
          userEmail={record.uploadedBy}
          className="text-ellipsis"
        />
      ),
    },
    {
      title: "Last Tool Scan",
      dataIndex: "scanDate",
      key: "scanDate",
      width: 150,
      sorter: true,
      render: (value) => dateFormatter.render("scanDate", value),
      dateFormatter: dateFormatter,
    },
    {
      title: "Tags",
      dataIndex: "tags",
      key: "tags",
      width: 120,
      render: (_, records) => {
        if (records.tags) {
          return (
            <ACResponsiveTags
              tags={records.tags}
              secondaryLabelDelimiter=":"
              secondaryTitle="Value"
              columnTitle="Key"
            />
          );
        }
        return CellRenderers.VALUE_OR_NA();
      },
    },
  ];

  return {
    allColumns: columns,
    actionRender: (record) => {
      const { progress } = getProgress(record.scanStatus);
      const { fileName: recordFileName } = record;
      const disableRedirect =
        !(progress > 66 && !checkOnlyDuplicateFindings(record.findings)) ||
        progress <= 66;
      const tooltip =
        progress <= 66
          ? `Scan ${_.capitalize(record.scanStatus)}`
          : checkOnlyDuplicateFindings(record.findings)
            ? "No new findings are found"
            : "View result details";
      const scanAge = moment().diff(moment(record.createdAt), "days");
      const isScanExpired = scanAge > MAX_SCAN_AGE_DAYS;

      let exportReportToolTip = "Export Report";
      if (isScanExpired) {
        exportReportToolTip = `${exportReportToolTip} disabled for scans older than ${MAX_SCAN_AGE_DAYS} days`;
      } else if (
        typeof recordFileName === "string" &&
        recordFileName.trim() !== ""
      ) {
        exportReportToolTip = `${exportReportToolTip} - ${recordFileName}`;
      }

      const actionItems: ACDropdownOptionProps[] = [
        {
          label: <Tooltip title={tooltip}>Result Details</Tooltip>,
          value: "resultDetail",
          disabled: disableRedirect ? true : false,
          icon: <EyeIcon />,
        },
        {
          label: <Tooltip title={exportReportToolTip}>Export Report</Tooltip>,
          value: "exportReport",
          disabled: isScanExpired,
          icon: <DownloadIcon />,
        },
        {
          label: (
            <Tooltip
              title={
                !hasPermission(PermissionSetMap.DELETE_SCAN) ? "No access" : ""
              }
            >
              Delete Scans
            </Tooltip>
          ),
          value: "delete",
          disabled: !hasPermission(PermissionSetMap.DELETE_SCAN),
          icon: <TrashIcon />,
        },
      ];

      return (
        <ACDropdown
          items={actionItems}
          onOptionClick={(key) => onActionClick(key, record)}
          placement="bottomRight"
        >
          <ACButton size="sm" iconOnly type="ghost">
            <MenuIcon />
          </ACButton>
        </ACDropdown>
      );
    },
  };
}

export const renderProductSubproductLink = (
  map: NameIdPair,
  path: string,
  onClick: () => void = () => {},
) => {
  return (
    <InternalLink
      to={`${path}/${map?.id}`}
      component={(props) => (
        <ACLink
          {...omit(props, ["navigate"])}
          primary={false}
          overlay
          onClick={onClick}
        />
      )}
    >
      {map?.name}
    </InternalLink>
  );
};
